import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './auth/AuthProvider';
import { auth } from '../firebase';
import { useRole } from '../hooks/useRole';
import { useHubSpotPortals } from '../hooks/useHubSpotPortals';
import { analyticsApi, teamsApi, portalSettingsApi } from '../services/api';
import { signOut } from 'firebase/auth';


const HUBSPOT_CONNECT_APP = 'v1';

function decodeJwt(token) {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

function StatTile({ value, label, valueClassName = 'text-gray-900' }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className={`text-3xl font-bold ${valueClassName}`}>{value}</div>
      <div className="text-sm text-gray-500 mt-1">{label}</div>
    </div>
  );
}


export default function Dashboard() {
  const { user } = useAuth();
  const { role } = useRole();
  const { portals, loading, error, selectedPortal, fetchPortals, selectPortal } = useHubSpotPortals();
  const navigate = useNavigate();

  const [selectedPortalId, setSelectedPortalId] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [connectingApp, setConnectingApp] = useState(null);
  //const [disconnecting, setDisconnecting] = useState(false);
  const portalSyncInFlightRef = useRef(null);

  const [healthStats, setHealthStats] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [teamCount, setTeamCount] = useState(null);
  const [hasRules, setHasRules] = useState(false);
  const [firstTeamId, setFirstTeamId] = useState(null);
  const [hasFallback, setHasFallback] = useState(false);
  const [uninstalling, setUninstalling] = useState(false);

  // Check Firebase token expiration
  useEffect(() => {
    if (!user) return;
    user.getIdToken().then(token => {
      const decoded = decodeJwt(token);
      if (decoded?.exp) {
        setSessionExpired(Date.now() >= decoded.exp * 1000);
      }
    });
  }, [user]);

  // Fetch portals on load
  useEffect(() => {
    fetchPortals();
  }, [fetchPortals]);

  // Ensure dropdown reflects currently active portal
  useEffect(() => {
    if (selectedPortal) {
      setSelectedPortalId(selectedPortal.portal_id);
    }
  }, [selectedPortal]);

  // Fetch team count (and rules check on first team) when portal changes
  useEffect(() => {
    if (!selectedPortal?.portal_id) return;
    setHasRules(false);
    setFirstTeamId(null);
    setHasFallback(false);
    portalSettingsApi.fetchPortalSettings(selectedPortal.portal_id)
      .then(result => setHasFallback(!!result?.settings?.fallback_owner_id))
      .catch(() => {});
    teamsApi.fetchTeams(selectedPortal.portal_id)
      .then(data => {
        const teams = data?.teams ?? (Array.isArray(data) ? data : []);
        setTeamCount(teams.length);
        const firstTeam = teams[0];
        if (!firstTeam?.hs_team_id) return;
        setFirstTeamId(firstTeam.id);
        return teamsApi.fetchTeamRules(firstTeam.hs_team_id)
          .then(rules => setHasRules((rules?.rules ?? (Array.isArray(rules) ? rules : [])).length > 0))
          .catch(() => {});
      })
      .catch(() => setTeamCount(0));
  }, [selectedPortal?.portal_id]);

  // Fetch routing health stats when portal changes
  useEffect(() => {
    if (!selectedPortal?.portal_id) return;

    async function fetchHealthStats() {
      setHealthLoading(true);
      try {
        const endISO = new Date().toISOString();
        const startISO = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
        const data = await analyticsApi.queryAnalytics({
          portalId: selectedPortal.portal_id,
          objectType: null,
          startISO,
          endISO,
          reportType: 'executive_summary',
        });
        setHealthStats(data);
      } catch {
        setHealthStats(null);
      } finally {
        setHealthLoading(false);
      }
    }

    fetchHealthStats();
  }, [selectedPortal?.portal_id]);

  const handlePortalSelect = () => {
    const portal = portals.find(p => p.portal_id === selectedPortalId);
    if (portal) selectPortal(portal.portal_id, portal.hub_domain);
  };

  useEffect(() => {
    if (!user || !selectedPortal?.portal_id) return;

    const params = new URLSearchParams(window.location.search);
    if (params.get('oauth_success') !== 'true') return;

    const portalId = String(selectedPortal.portal_id);
    if (portalSyncInFlightRef.current === portalId) {
      params.delete('oauth_success');
      params.delete('portal');
      const nextUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
      window.history.replaceState({}, '', nextUrl);
      return;
    }

    let cancelled = false;

    async function kickoffPortalAccountObjectSync() {
      try {
        portalSyncInFlightRef.current = portalId;
        const token = await user.getIdToken();
        const response = await fetch(`${process.env.REACT_APP_API_URL}/portal-account-object/sync`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ portalId }),
        });

        if (!response.ok) {
          throw new Error('Failed to queue portal account object sync');
        }
      } catch (err) {
        console.error('Portal account object sync kickoff failed:', err);
      } finally {
        if (!cancelled) {
          portalSyncInFlightRef.current = null;
          params.delete('oauth_success');
          params.delete('portal');
          const nextUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
          window.history.replaceState({}, '', nextUrl);
        }
      }
    }

    kickoffPortalAccountObjectSync();

    return () => {
      cancelled = true;
    };
  }, [user, selectedPortal?.portal_id]);

  const handleConnectPortal = async (app) => {
    try {
      setConnectingApp(app);
      const token = await user.getIdToken();
      const res = await fetch(`${process.env.REACT_APP_API_URL}/hubspot-oauth/initiate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ app }),
      });
      if (!res.ok) throw new Error('Failed to initiate OAuth');
      const { redirectUrl } = await res.json();
      window.location.href = redirectUrl;
    } catch (err) {
      console.error('OAuth initiation failed:', err);
      alert('Failed to connect to HubSpot. Please try again.');
      setConnectingApp(null);
    }
  };

  const handleLogout = () => {
    signOut(auth);
  };

  const handleUninstall = async () => {
    if (!selectedPortal) return;
    const confirmed = window.confirm(
      `Uninstall Distributely from ${selectedPortal.hub_domain} (${selectedPortal.portal_id})?\n\nThis will remove the Distributely app from your HubSpot portal. This action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      setUninstalling(true);
      const token = await user.getIdToken();
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/hubspot-oauth/uninstall/${selectedPortal.portal_id}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await res.json();
      if (data.success) {
        setSelectedPortalId('');
        await fetchPortals();
        alert('Distributely has been uninstalled from your HubSpot portal.');
      } else {
        // Still refresh portal list — DB cleanup may have succeeded even if HubSpot call failed
        setSelectedPortalId('');
        await fetchPortals();
        alert(`Uninstall failed: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Uninstall failed:', err);
      alert('Failed to uninstall. Please try again.');
    } finally {
      setUninstalling(false);
    }
  };


  // const handleDisconnectPortal = async () => {
  //   if (!selectedPortal) return;
  //   const confirmed = window.confirm(
  //     `Disconnect ${selectedPortal.hub_domain} (${selectedPortal.portal_id})?\n\nThis will remove the HubSpot connection for your account. You can reconnect at any time.`
  //   );
  //   if (!confirmed) return;
  //
  //   try {
  //     setDisconnecting(true);
  //     const token = await user.getIdToken();
  //     const res = await fetch(
  //       `${process.env.REACT_APP_API_URL}/hubspot-oauth/disconnect/${selectedPortal.portal_id}`,
  //       {
  //         method: 'DELETE',
  //         headers: { Authorization: `Bearer ${token}` },
  //       }
  //     );
  //     if (!res.ok) throw new Error('Disconnect failed');
  //     await fetchPortals();
  //   } catch (err) {
  //     console.error('Portal disconnect failed:', err);
  //     alert('Failed to disconnect portal. Please try again.');
  //   } finally {
  //     setDisconnecting(false);
  //   }
  // };

  // Derive routing health values from executive_summary response
  const recordsRouted = healthStats?.total ?? '—';
  const assignedPct = healthStats?.successRate != null
    ? `${(healthStats.successRate * 100).toFixed(1)}%`
    : '—';
  const activeTeams = healthStats?.byTeam != null ? healthStats.byTeam.length : '—';
  const fallbackHits = healthStats?.failure ?? '—';

  const stepsComplete = [!!selectedPortal, teamCount > 0, hasRules, hasFallback].filter(Boolean).length;

  return (
    <div className="max-w-5xl mx-auto">
      {/* Page Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Dashboard</h1>
          <p className="text-gray-500">Welcome back, {user?.displayName || user?.email} — here's the state of your routing setup.</p>
        </div>

        {role && (
          <div className="flex items-center space-x-3 mt-1">
            <span className="text-sm text-gray-500">Role</span>
            <span className="text-sm font-semibold text-gray-900">
              {role === 'SUPER_ADMIN' ? 'Super Admin' : role === 'ADMIN' ? 'Admin' : 'User'}
            </span>
            <span className={`flex items-center space-x-1 text-sm font-medium ${sessionExpired ? 'text-red-600' : 'text-green-600'}`}>
              <span className={`inline-block w-2 h-2 rounded-full ${sessionExpired ? 'bg-red-500' : 'bg-green-500'}`} />
              <span>{sessionExpired ? 'Session expired' : 'Session active'}</span>
            </span>
          </div>
        )}
      </div>

      {/* Finish setting up Distributely */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-1">Finish setting up Distributely</h2>
            <p className="text-sm text-gray-500">Complete these steps to start routing records automatically.</p>
          </div>
          <div className="text-right flex-shrink-0 ml-6">
            <span className="text-sm font-semibold text-blue-600">{stepsComplete} of 4 complete</span>
            <div className="mt-2 w-48 bg-gray-200 rounded-full h-2">
              <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${(stepsComplete / 4) * 100}%` }} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Step 1 — connect portal */}
          <div className="flex items-start justify-between border border-gray-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-0.5">
                {selectedPortal ? (
                  <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                )}
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Connect your HubSpot portal</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {selectedPortal ? `${selectedPortal.hub_domain} (${selectedPortal.portal_id})` : 'No portal connected yet'}
                </p>
              </div>
            </div>
            {selectedPortal
              ? <span className="text-xs font-semibold text-green-600 flex-shrink-0 ml-3">Done</span>
              : <button className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex-shrink-0 ml-3" onClick={() => handleConnectPortal(HUBSPOT_CONNECT_APP)}>Connect</button>
            }
          </div>

          {/* Step 2 — create team */}
          <div className="flex items-start justify-between border border-gray-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-0.5">
                {teamCount > 0 ? (
                  <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                )}
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Create your first team</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {teamCount > 0 ? 'First team is set up and ready to distribute!' : 'No teams set up yet'}
                </p>
              </div>
            </div>
            {teamCount > 0
              ? <span className="text-xs font-semibold text-green-600 flex-shrink-0 ml-3">Done</span>
              : <button className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex-shrink-0 ml-3" onClick={() => navigate('/teams')}>Set up</button>
            }
          </div>

          {/* Step 3 — routing rules */}
          <div className="flex items-start justify-between border border-gray-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-0.5">
                {hasRules ? (
                  <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                )}
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Configure routing rules</p>
                <p className="text-xs text-gray-400 mt-0.5">Decide which records go to which team</p>
              </div>
            </div>
            {hasRules
              ? <span className="text-xs font-semibold text-green-600 flex-shrink-0 ml-3">Done</span>
              : <button className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex-shrink-0 ml-3" onClick={() => navigate(firstTeamId ? `/teams/${firstTeamId}/rules` : '/teams')}>Set up</button>
            }
          </div>

          {/* Step 4 — fallback owner */}
          <div className="flex items-start justify-between border border-gray-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-0.5">
                {hasFallback ? (
                  <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                )}
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Set a portal fallback owner</p>
                <p className="text-xs text-gray-400 mt-0.5">Catch records no team can take</p>
              </div>
            </div>
            {hasFallback
              ? <span className="text-xs font-semibold text-green-600 flex-shrink-0 ml-3">Done</span>
              : <button className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex-shrink-0 ml-3" onClick={() => navigate('/settings/portal')}>Configure</button>
            }
          </div>
        </div>
      </div>

      {/* HubSpot Portal + Routing Health */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-6">

        {/* HubSpot Portal card — 3 cols */}
        <div className="lg:col-span-3 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-1">HubSpot Portal</h2>
          <p className="text-sm text-gray-500 mb-4">Distributely is connected and routing records in this portal.</p>

          {loading && <p className="text-gray-400 text-sm">Loading portals...</p>}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {selectedPortal && (
            <div className="flex items-center space-x-3 bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="bg-blue-100 text-blue-600 rounded-full p-2 flex-shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="font-semibold text-blue-800">Active portal: {selectedPortal.hub_domain}</p>
                <p className="text-sm text-blue-600">Portal ID {selectedPortal.portal_id}</p>
              </div>
            </div>
          )}

          {portals.length > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Switch active portal</label>
              <select
                value={selectedPortalId}
                onChange={(e) => setSelectedPortalId(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition duration-200 text-sm"
              >
                <option value="">Select a HubSpot Portal...</option>
                {portals.map(portal => (
                  <option key={portal.portal_id} value={portal.portal_id}>
                    {portal.hub_domain} (ID: {portal.portal_id})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex space-x-3">
            <button
              onClick={handlePortalSelect}
              disabled={!selectedPortalId}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold py-2.5 px-4 rounded-lg transition duration-200 text-sm"
            >
              Set Active Portal
            </button>
            <button
              onClick={() => handleConnectPortal(HUBSPOT_CONNECT_APP)}
              disabled={connectingApp !== null}
              className="flex-1 border border-orange-500 text-orange-600 hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed font-semibold py-2.5 px-4 rounded-lg transition duration-200 text-sm flex items-center justify-center space-x-1"
            >
              <span>+</span>
              <span>{connectingApp ? 'Redirecting...' : 'Connect HubSpot'}</span>
            </button>
            {/*{selectedPortal && (*/}
            {/*  <button*/}
            {/*    onClick={handleDisconnectPortal}*/}
            {/*    disabled={disconnecting}*/}
            {/*    className="border border-red-400 text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed font-semibold py-2.5 px-4 rounded-lg transition duration-200 text-sm"*/}
            {/*  >*/}
            {/*    {disconnecting ? 'Disconnecting...' : 'Disconnect'}*/}
            {/*  </button>*/}
            {/*)}*/}
          </div>
        </div>

        {/* Routing Health card — 2 cols */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Routing health</h2>
            <span className="text-xs text-gray-400">Last 7 days</span>
          </div>

          {healthLoading ? (
            <div className="grid grid-cols-2 gap-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-gray-100 rounded-lg p-4 h-20 animate-pulse" />
              ))}
            </div>
          ) : !selectedPortal ? (
            <p className="text-sm text-gray-400">Select a portal to see routing health.</p>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <StatTile value={recordsRouted} label="Records routed" />
              <StatTile value={assignedPct} label="Assigned successfully" valueClassName="text-green-600" />
              <StatTile value={activeTeams} label="Active teams" />
              <StatTile value={fallbackHits} label="Fallback hits" valueClassName={fallbackHits > 0 ? 'text-orange-500' : 'text-gray-900'} />
            </div>
          )}

          {selectedPortal && !healthLoading && (
            <button
              onClick={() => navigate('/analytics')}
              className="w-full mt-4 border border-gray-200 text-blue-600 hover:bg-gray-50 font-medium py-2 rounded-lg text-sm transition duration-200"
            >
              View full analytics
            </button>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Quick actions</h2>
          <button
            onClick={handleLogout}
            className="text-sm text-gray-400 hover:text-gray-600 transition duration-200"
          >
            Log out
          </button>
        </div>

      {/*  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">*/}
      {/*    <QuickActionTile*/}
      {/*      icon={*/}
      {/*        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">*/}
      {/*          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />*/}
      {/*        </svg>*/}
      {/*      }*/}
      {/*      label="Add Team"*/}
      {/*      description="Create a routing team"*/}
      {/*      onClick={() => navigate('/teams')}*/}
      {/*    />*/}
      {/*    <QuickActionTile*/}
      {/*      icon={*/}
      {/*        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">*/}
      {/*          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h10" />*/}
      {/*        </svg>*/}
      {/*      }*/}
      {/*      label="Add Rule"*/}
      {/*      description="Route records by field"*/}
      {/*      onClick={() => navigate(firstTeamId ? `/teams/${firstTeamId}/rules` : '/teams')}*/}
      {/*      disabled={!teamCount}*/}
      {/*    />*/}
      {/*    <QuickActionTile*/}
      {/*      icon={*/}
      {/*        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">*/}
      {/*          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />*/}
      {/*        </svg>*/}
      {/*      }*/}
      {/*      label="View Analytics"*/}
      {/*      description="See distribution results"*/}
      {/*      onClick={() => navigate('/analytics')}*/}
      {/*    />*/}
      {/*    <QuickActionTile*/}
      {/*      icon={*/}
      {/*        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">*/}
      {/*          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />*/}
      {/*        </svg>*/}
      {/*      }*/}
      {/*      label="Manage Members"*/}
      {/*      description="Edit owners & weights"*/}
      {/*      onClick={() => navigate('/agents')}*/}
      {/*    />*/}
      {/*  </div>*/}
      </div>

      {/* Footer */}
      {selectedPortal && (
        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
          <button
            onClick={handleUninstall}
            disabled={uninstalling}
            className="text-sm text-gray-400 hover:text-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
          >
            {uninstalling ? 'Uninstalling...' : 'Uninstall Distributely'}
          </button>
        </div>
      )}
    </div>
  );
}
